import type { IOrderDetail } from '../graphQL/queries/order/types'
import { SessionCheckoutRuleType } from '../graphQL/queries/getSession/types'

/**
 * The picked payment option's label on a placed order, when the shopper's
 * `PAYMENT_METHODS` pick carried one (guide §5, COM-1259).
 *
 * `Order.checkoutRules` mirrors the session's: the storefront persists the
 * pick on `Session.checkoutRules[].value` through `setCheckoutRuleSession`
 * and the order resolver copies it forward at create time, so a keyed pick's
 * `attributes.label` — "Crédito 14 días" over a `data: 'CREDIT'` row — rides
 * through verbatim and is the correct string to render against the order's
 * bare `paymentMethod` enum. Mirrors `selectRulePickLabel`
 * (`utils/checkoutRuleSelection.ts`), which does the same job for the cart's
 * `Pago` row off `checkoutSlice.available` and the session's own pick. The
 * two cannot share a reader: the cart's data lives in Redux while this one's
 * lives on the order itself, so an Order Detail opened later, for an order
 * the current session no longer carries, has no other source.
 *
 * **The sibling injectors forward the return as `paymentMethodLabel` on
 * `OrderConfirmationPanel` and `OrderDetailPanel`.** The library's precedence
 * is: a non-empty override wins over `paymentMethodLabels[method]` and over
 * the raw method (COM-1252, `@engyalo/delivery-ui-components@1.25.0`). Returning
 * `undefined` therefore leaves the panel on today's `paymentMethodLabels[method]`
 * lookup — the "orders without a keyed pick show today's label" acceptance
 * criterion.
 *
 * ⚠️ **An entry is not a pick.** `value` is a nullable
 * `IOrderCheckoutRuleValue`, `attributes` is nullable under that, and
 * `attributes.label` is itself optional. A rule the storefront carried without
 * a keyed label — the pilot shape, where the pick is `{ data: 'CREDIT' }` with
 * no `attributes` — must read as "no override" rather than throw. Read the
 * chain through optional access and reject the empty string the same way
 * `selectRulePickLabel` and the library's `resolvePaymentLabel` do; a non-empty
 * string is the panel's "override is set" signal.
 *
 * **Match by `ruleType` only, not by `key`.** The session carries at most one
 * entry per `ruleType` (`setCheckoutRuleById` filters and appends
 * server-side), so there is nothing to disambiguate a keyed `CREDIT` from an
 * unkeyed one at the order level — the pick is already resolved. `key` on the
 * value is informational on the order and is not read here.
 */
export const getOrderPaymentPickLabel = (
  order: IOrderDetail | null | undefined,
): string | undefined => {
  const rules = order?.checkoutRules
  if (!Array.isArray(rules) || rules.length === 0) return undefined

  const match = rules.find(
    (rule) => rule?.ruleType === SessionCheckoutRuleType.PAYMENT_METHODS,
  )
  const label = match?.value?.attributes?.label

  return typeof label === 'string' && label !== '' ? label : undefined
}

export default getOrderPaymentPickLabel
